import logger from "@/core/logger"
import {
  CartItem,
  EventRequestMessageV1,
  CustomerAffinityResponse,
  DebugToolbarDataDTO,
  EventResponseMessage,
  Experiment,
  PushedCustomer,
  SegmentsResponseBean,
  CategoryClick,
  SearchClick,
  SearchImpression,
  CategoryImpression,
  Expect
} from "@/types"
import type { Level } from "../logger/types"
import type { OrderError } from "@/order/track"
import type { SearchQuery, SearchResult } from "@/search/types"
import type { TaggingData } from "../tagging/types"
import { isPromise } from "@/utils/isPromise"

/**
 * Payload for prerender event
 *
 * @group Core
 */
export interface Prerender {
  customerId?: string
  affinityScores: CustomerAffinityResponse
  geoLocation: string[]
  eventDate: Date
  pageViews: number
  segments: SegmentsResponseBean
}

/**
 * Payload for postrender event
 *
 * @group Core
 */
export interface Postrender {
  responseData: Record<string, unknown>
  filledElements: string[]
  unFilledElements: string[]
}

/**
 * Payload for setexperiments event
 *
 * @group Core
 */
export interface Setexperiments {
  experiments: Experiment[]
}

/**
 * Payload for coupongiven event
 *
 * @group Core
 */
export interface Coupongiven {
  coupon_code: string
  coupon_campaign: string
  coupon_used: boolean
}

/**
 * Payload for scripterror event
 *
 * @group Core
 */
export interface Scripterror {
  msg: string
  stack?: string
  level: Level
}

/**
 * Payload for addtocart event
 *
 * @group Core
 */
export interface Addtocart {
  productId: string
  skuId?: string
  // misleading, since this is the recoId
  placementId: string
}

/**
 * Payload for carttaggingresent event
 *
 * @group Core
 */
export interface Carttaggingresent {
  cart_items: CartItem[]
  restore_link?: string
}

/**
 * Payload for setsegments event
 *
 * @group Core
 */
export interface Segments {
  segment: string
}

export interface SearchSuccessEventDTO {
  query: SearchQuery
  graphqlQuery: string
  graphqlVariables: object
  response: SearchResult
}

export interface SearchFailureEventDTO {
  query: SearchQuery
  graphqlQuery: string
  graphqlVariables: object
  error: string
}

/**
 * Payload for popupopened event
 *
 * @group Core
 */
export interface Popupopened {
  campaignId: string
  error?: unknown
  type: "api" | string // TODO: Should be made stricter
}

/**
 * Payload for popupmaximized, popupminimized, popupclosed and popupribbonshown events
 *
 * @group Core
 */
export interface Popup {
  campaignId: string
}

type LifecyleEvents = {
  prerequest: [EventRequestMessageV1]
  prerender: [Prerender]
  postrender: [Postrender]
  taggingsent: [EventResponseMessage]
  taggingresent: [TaggingData]
  carttaggingresent: [Carttaggingresent]
  customertaggingresent: [PushedCustomer]
  emailgiven: [PushedCustomer]
  scripterror: [Scripterror]
  servererror: [string[]]
}

type PopupEvents = {
  popupopened: [Popupopened]
  popupmaximized: [Popup]
  popupminimized: [Popup]
  coupongiven: [Coupongiven]
  popupclosed: [Popup]
  popupribbonshown: [Popup]
  sendabandonedcartemail: [object]
}

type InternalEvents = {
  ordererror: [OrderError]
  setexperiments: [Setexperiments]
  setsegments: [Segments]
  setcustomaffinities: []
  setcart: []
  addtocart: [Addtocart]
  ev1end: []
  debugdata: [DebugToolbarDataDTO]
  searchsuccess: [SearchSuccessEventDTO]
  searchfailure: [SearchFailureEventDTO]
  searchclick: [SearchClick]
  searchaddtocart: [SearchClick]
  searchimpression: [SearchImpression]
  categoryclick: [CategoryClick]
  categoryaddtocart: [CategoryClick]
  categoryimpression: [CategoryImpression]
  reloadrequested: []
}

type TaggingEvents = {
  cartupdated: []
}

type ConsentEvents = {
  analyticsallowed: [boolean]
  marketingallowed: [boolean]
}

/**
 * Mapping from event name to payload type
 *
 * @interface
 */
export type EventMapping = LifecyleEvents & PopupEvents & InternalEvents & TaggingEvents & ConsentEvents

/**
 * Keys of {@link EventMapping}
 */
export type BusEvent =
  | "prerequest"
  | "prerender"
  | "postrender"
  | "taggingsent"
  | "taggingresent"
  | "carttaggingresent"
  | "customertaggingresent"
  | "emailgiven"
  | "scripterror"
  | "servererror"
  | "popupopened"
  | "popupmaximized"
  | "popupminimized"
  | "coupongiven"
  | "popupclosed"
  | "popupribbonshown"
  | "sendabandonedcartemail"
  | "ordererror"
  | "setexperiments"
  | "setsegments"
  | "setcustomaffinities"
  | "setcart"
  | "addtocart"
  | "ev1end"
  | "debugdata"
  | "searchsuccess"
  | "searchfailure"
  | "searchclick"
  | "searchaddtocart"
  | "searchimpression"
  | "categoryclick"
  | "categoryaddtocart"
  | "categoryimpression"
  | "cartupdated"
  | "reloadrequested"
  | "analyticsallowed"
  | "marketingallowed"

export type Callback<T extends BusEvent> = (...args: EventMapping[T]) => void
type Callbacks = { [K in BusEvent]?: Callback<K>[] }
type Payloads = { [K in BusEvent]?: EventMapping[K] }

const callbacks: Callbacks = {}
const lastPayload: Payloads = {}

async function invoke<T extends BusEvent>(event: T, fn: Callback<T>, args: EventMapping[T]) {
  try {
    const result = fn(...args)
    if (isPromise(result)) {
      // await promise to get the rejection also logged
      await result
    }
  } catch (e) {
    logger.warn(`Error in ${event} listener`, e)
  }
}

function normalize<T extends string>(event: T) {
  return event.toLowerCase() as T
}

const bus = {
  on<T extends BusEvent>(e: T, fn: Callback<T>) {
    const event = normalize(e)
    callbacks[event] = callbacks[event] || []
    callbacks[event]!.push(fn)
    if (lastPayload[event]) {
      void invoke(event, fn, lastPayload[event]!)
    }
  },
  off<T extends BusEvent>(e: T, fn: Callback<T>) {
    const event = normalize(e)
    if (!callbacks[event]) {
      return
    }
    const index = callbacks[event]!.indexOf(fn)
    if (index !== -1) {
      callbacks[event]!.splice(index, 1)
    }
  },
  emit<T extends BusEvent>(e: T, ...args: EventMapping[T]) {
    const event = normalize(e)
    lastPayload[event] = args
    callbacks[event]?.forEach(fn => invoke(event, fn, args))
  }
}

/** @hidden */
export type tests = [
  Expect<

      | keyof LifecyleEvents
      | keyof PopupEvents
      | keyof InternalEvents
      | keyof TaggingEvents
      | keyof ConsentEvents extends BusEvent
      ? true
      : false
  >
]

export default bus
